import React, { useState, useEffect } from "react";
import {
    Container,
    Grid,
    Button,
    FormControl,
    Typography,
    TextField,
    MenuItem,
    Chip,
    Alert,
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import { useNavigate, useLocation } from 'react-router-dom';
import { GetIntegrations, SaveIntegration, DeleteIntegration, InitiateOAuth, AuthenticateIntegration } from "../../../../api";

const INTEGRATION_TYPES = [
    { value: 'provet', label: 'ProVet Cloud' },
    { value: 'instinct', label: 'Instinct' },
    { value: 'ezyvet', label: 'EzyVet' },
]

const defaultConfig = {
    provet: { provetId: '' },
    instinct: { username: '', password: '' },
    ezyvet: { siteUid: '' }
}

const Integrations = ({ user, access }) => {
    const [integrations, setIntegrations] = useState([])
    const [type, setType] = useState('provet')
    const [config, setConfig] = useState({ provetId: '' })
    const [associateOrg, setAssociateOrg] = useState('')
    const [editing, setEditing] = useState(null) // integration id or 'new'
    const navigate = useNavigate()

    const clinicOrgs = user.access.filter(e => e.clinic)
    const location = useLocation()
    const oauthStatus = React.useMemo(() => {
        const p = new URLSearchParams(location.search)
        if (p.get('success')) return { type: 'success', msg: 'Authorization successful.' }
        if (p.get('error')) return { type: 'error', msg: `Authorization failed: ${p.get('error')}` }
        return null
    }, [location.search])

    useEffect(() => {
        if (!access?.admin) { navigate('/', { replace: true }); return }
        GetIntegrations(access.org.id).then(setIntegrations).catch(() => setIntegrations([]))
    }, [access, navigate])

    const startNew = () => {
        setType('provet')
        setConfig({ provetId: '' })
        setAssociateOrg('')
        setEditing('new')
    }

    const startEdit = (integration) => {
        setType(integration.type)
        setConfig(integration.config || defaultConfig[integration.type] || {})
        setAssociateOrg(integration.associateOrg || '')
        setEditing(integration.type)
    }

    const handleSave = () => {
        SaveIntegration(access.org.id, { type, config, associateOrg }).then(saved => {
            setIntegrations(prev => {
                const i = prev.findIndex(x => x.type === type)
                return i >= 0 ? prev.map((x, idx) => idx === i ? saved : x) : [...prev, saved]
            })
            setEditing(null)
        }).catch(() => {})
    }

    const handleDelete = (intType) => {
        DeleteIntegration(access.org.id, intType).then(() => {
            setIntegrations(prev => prev.filter(x => x.type !== intType))
            if (editing === intType) setEditing(null)
        }).catch(() => {})
    }

    const handleAuthenticate = (intType) => {
        AuthenticateIntegration(access.org.id, intType).then(() => {
            setIntegrations(prev => prev.map(x => x.type === intType ? { ...x, connected: true } : x))
        }).catch(() => {})
    }

    const handleOAuth = (intType) => {
        InitiateOAuth(access.org.id, intType).then(resp => {
            if (resp?.url) window.location.href = resp.url
        }).catch(() => {})
    }

    const getAssociateOrgName = (orgId) => {
        const a = user.access.find(e => e.org.id === orgId)
        return a?.org.name || orgId
    }

    return (
        <Container>
            <Grid container sx={{ mb: 2 }} justifyContent="space-between" alignItems="center">
                <Grid item>
                    <Typography component="h1" variant="h4">Integrations</Typography>
                </Grid>
                {!editing && (
                    <Grid item>
                        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={startNew}
                            disabled={integrations.length >= INTEGRATION_TYPES.length}>
                            Add Integration
                        </Button>
                    </Grid>
                )}
            </Grid>
            <Grid container justifyContent="left" maxWidth="sm" sx={{ mb: 2 }}>
                {oauthStatus && <Alert severity={oauthStatus.type} sx={{ mb: 2, width: '100%' }}>{oauthStatus.msg}</Alert>}

                {integrations.map(integration => (
                    <Grid key={integration.type} container sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }} maxWidth="sm">
                        <Grid size={12} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="h6">{INTEGRATION_TYPES.find(t => t.value === integration.type)?.label || integration.type}</Typography>
                            {integration.connected && <Chip label="Connected" color="success" size="small" />}
                        </Grid>
                        {integration.associateOrg && (
                            <Grid size={12} sx={{ mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Referring Org: {getAssociateOrgName(integration.associateOrg)}
                                </Typography>
                            </Grid>
                        )}
                        <Grid container spacing={1}>
                            <Button size="small" startIcon={<SaveIcon />} onClick={() => startEdit(integration)}>Edit</Button>
                            {integration.type === 'instinct' || integration.type === 'ezyvet' ? (
                                <Button size="small" startIcon={<LinkIcon />} onClick={() => handleAuthenticate(integration.type)}>
                                    {integration.connected ? 'Re-authenticate' : 'Authenticate'}
                                </Button>
                            ) : (
                                <Button size="small" startIcon={<LinkIcon />} onClick={() => handleOAuth(integration.type)}>
                                    {integration.connected ? 'Re-authorize' : 'Authorize'}
                                </Button>
                            )}
                            <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleDelete(integration.type)}>Remove</Button>
                        </Grid>
                    </Grid>
                ))}

                {editing && (
                    <Grid container maxWidth="sm" sx={{ mt: 2 }} spacing={2}>
                        <Typography component="h2" variant="h5">{editing === 'new' ? 'Add Integration' : 'Edit Integration'}</Typography>
                        {editing === 'new' && (
                            <FormControl fullWidth variant="standard">
                                <TextField
                                    label="Integration Type"
                                    value={type}
                                    onChange={e => { setType(e.target.value); setConfig(defaultConfig[e.target.value] || {}) }}
                                    select
                                    variant="standard"
                                >
                                    {INTEGRATION_TYPES.filter(t => editing === 'new' ? !integrations.find(i => i.type === t.value) : true).map(t => (
                                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                    ))}
                                </TextField>
                            </FormControl>
                        )}
                        {type === 'provet' && (
                            <FormControl fullWidth variant="standard">
                                <TextField
                                    label="ProVet ID"
                                    value={config.provetId || ''}
                                    onChange={e => setConfig(c => ({ ...c, provetId: e.target.value }))}
                                    variant="standard"
                                    helperText="Your ProVet Cloud instance ID (e.g. from provetcloud.com/<your-id>/)"
                                    required
                                />
                            </FormControl>
                        )}
                        {type === 'instinct' && (<>
                            <FormControl fullWidth variant="standard">
                                <TextField
                                    label="Username"
                                    value={config.username || ''}
                                    onChange={e => setConfig(c => ({ ...c, username: e.target.value }))}
                                    variant="standard"
                                    required
                                />
                            </FormControl>
                            <FormControl fullWidth variant="standard">
                                <TextField
                                    label="Password"
                                    value={config.password || ''}
                                    onChange={e => setConfig(c => ({ ...c, password: e.target.value }))}
                                    variant="standard"
                                    type="password"
                                    required
                                />
                            </FormControl>
                        </>)}
                        {type === 'ezyvet' && (
                            <FormControl fullWidth variant="standard">
                                <TextField
                                    label="Site UID"
                                    value={config.siteUid || ''}
                                    onChange={e => setConfig(c => ({ ...c, siteUid: e.target.value }))}
                                    variant="standard"
                                    helperText="EzyVet-issued site_uid for this clinic"
                                    required
                                />
                            </FormControl>
                        )}
                        <FormControl fullWidth variant="standard">
                            <TextField
                                label="Referring Organization"
                                value={associateOrg}
                                onChange={e => setAssociateOrg(e.target.value)}
                                select
                                variant="standard"
                                helperText="Default referring org for PIMS-created requests"
                            >
                                <MenuItem value=""><em>None</em></MenuItem>
                                {clinicOrgs.map(e => (
                                    <MenuItem key={e.org.id} value={e.org.id}>{e.org.name}</MenuItem>
                                ))}
                            </TextField>
                        </FormControl>
                        <Grid container spacing={1} sx={{ mt: 1 }}>
                            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}
                                disabled={
                                    (type === 'provet' && !config.provetId) ||
                                    (type === 'instinct' && (!config.username || !config.password)) ||
                                    (type === 'ezyvet' && !config.siteUid)
                                }>Save</Button>
                            <Button variant="outlined" onClick={() => setEditing(null)}>Cancel</Button>
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </Container>
    )
}

export default Integrations
